import React, { createContext, useContext, useState } from "react";

const CartContext = createContext();

/*
  Har cart item ke liye unique key banayenge.

  Example:
  Product ID: 201
  Size: Custom
  Measurements: Bust 36, Waist 30...

  Agar same product ki measurements different hain,
  to cart mein wo alag item rahega.
*/
const createCartItemKey = (product) => {
  const measurements = product.customMeasurements
    ? JSON.stringify(product.customMeasurements)
    : "";

  const customization = product.customization
    ? JSON.stringify(product.customization)
    : "";

  return [
    product.id,
    product.selectedSize || "",
    measurements,
    customization,
  ].join("___");
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // ==============================
  // ADD TO CART
  // ==============================
  const addToCart = (product) => {
    setCartItems((prevItems) => {
      const newCartItemKey = createCartItemKey(product);

      const existingProduct = prevItems.find(
        (item) => item.cartItemKey === newCartItemKey
      );

      // Same product + same size + same customization
      if (existingProduct) {
        return prevItems.map((item) =>
          item.cartItemKey === newCartItemKey
            ? {
                ...item,
                quantity: Number(item.quantity || 0) + 1,
              }
            : item
        );
      }

      // New cart item
      return [
        ...prevItems,
        {
          ...product,
          price: Number(product.price) || 0,
          quantity: 1,
          cartItemKey: newCartItemKey,
        },
      ];
    });
  };

  // ==============================
  // INCREASE QUANTITY
  // ==============================
  const increaseQuantity = (cartItemKey) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.cartItemKey === cartItemKey
          ? {
              ...item,
              quantity: Number(item.quantity || 0) + 1,
            }
          : item
      )
    );
  };

  // ==============================
  // DECREASE QUANTITY
  // ==============================
  const decreaseQuantity = (cartItemKey) => {
    setCartItems((prevItems) =>
      prevItems
        .map((item) =>
          item.cartItemKey === cartItemKey
            ? {
                ...item,
                quantity: Number(item.quantity || 0) - 1,
              }
            : item
        )
        .filter((item) => Number(item.quantity || 0) > 0)
    );
  };

  // ==============================
  // REMOVE FROM CART
  // ==============================
  const removeFromCart = (cartItemKey) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) => item.cartItemKey !== cartItemKey
      )
    );
  };

  // ==============================
  // CLEAR CART
  // ==============================
  const clearCart = () => {
    setCartItems([]);
  };

  // ==============================
  // CART COUNT
  // ==============================
  const cartCount = cartItems.reduce(
    (total, item) =>
      total + Number(item.quantity || 0),
    0
  );

  // ==============================
  // CART TOTAL
  // ==============================
  const cartTotal = cartItems.reduce(
    (total, item) =>
      total +
      Number(item.price || 0) *
        Number(item.quantity || 0),
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        increaseQuantity,
        decreaseQuantity,
        removeFromCart,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

